import React, { useEffect, useState } from 'react';
import HtmlMessage from '../html-message';
import './index.css';

/**
 * A result the agent shows one page at a time.
 *
 * content: {
 *   view: 'table' | 'html',
 *   title?, displayText?,
 *   columns: [{ key, label }] | ['Name', ...],   // table view
 *   rows: [{ key: value }] | [[value, ...]],      // table view
 *   html: '<table>…</table>',                     // html view (sanitized)
 *   pagination?: { hasNext, hasPrev, label? },   // cursor/object paging
 *   page, pageSize?, totalItems?, totalPages?, hasNext?   // numbered paging
 * }
 * metadata.pagination.handle — set by the runtime; it is the only thing a page
 * turn sends back, so a message without one has no pager.
 *
 * With `pagination`, the step's next/prev page objects (a cursor, an offset,
 * any shape) stay on the server - the runtime replaces them with hasNext /
 * hasPrev - so a click sends a direction, never a value. Without it, pages are
 * numbered and a click sends the page number.
 *
 * A page arrives as a new PAGINATED message carrying the same handle; the chat
 * replaces this message with it rather than appending (see ChatPluginTask).
 */

function columnsOf(content) {
  const columns = Array.isArray(content.columns) ? content.columns : [];
  if (columns.length > 0) {
    return columns.map((column, index) =>
      typeof column === 'string' ? { key: index, label: column } : { key: column.key, label: column.label ?? column.key }
    );
  }
  const first = Array.isArray(content.rows) ? content.rows[0] : null;
  return first && !Array.isArray(first) && typeof first === 'object'
    ? Object.keys(first).map(key => ({ key, label: key }))
    : [];
}

function cellValue(row, column, index) {
  const value = Array.isArray(row) ? row[index] : row?.[column.key];
  if (value === null || value === undefined) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

export function pageInfo(content) {
  const pagination = content.pagination;
  if (pagination && typeof pagination === 'object') {
    return {
      label: pagination.label || '',
      hasNext: Boolean(pagination.hasNext),
      hasPrev: Boolean(pagination.hasPrev),
      next: { direction: 'next' },
      prev: { direction: 'prev' },
    };
  }

  const page = Number(content.page) || 1;
  const pageSize = Number(content.pageSize) || 0;
  const totalItems = Number(content.totalItems);
  let totalPages = Number(content.totalPages) || null;
  if (!totalPages && pageSize > 0 && Number.isFinite(totalItems)) {
    totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  }
  const hasNext = typeof content.hasNext === 'boolean' ? content.hasNext : (totalPages ? page < totalPages : false);
  return {
    label: totalPages ? `Page ${page} of ${totalPages}` : `Page ${page}`,
    hasNext,
    hasPrev: page > 1,
    next: { page: page + 1 },
    prev: { page: page - 1 },
  };
}

export default function PaginatedMessage({ message, metadata, onPaginate, disabled, replyPending }) {
  const content = message && typeof message === 'object' ? message : {};
  const handle = metadata?.pagination?.handle;
  const { label, hasNext, hasPrev, next, prev } = pageInfo(content);

  // The page being fetched. Cleared when the replacement message lands, or
  // when the chat stops waiting for a reply without one - the runtime answers
  // an expired list with a plain TEXT message instead.
  const [pending, setPending] = useState(null);
  useEffect(() => setPending(null), [message, handle]);
  useEffect(() => { if (!replyPending) setPending(null); }, [replyPending]);

  const go = target => {
    if (!handle || pending || disabled || typeof onPaginate !== 'function') return;
    setPending(target);
    Promise.resolve(onPaginate(handle, target)).catch(() => setPending(null));
  };

  const columns = columnsOf(content);
  const rows = Array.isArray(content.rows) ? content.rows : [];

  return (
    <div className={`conv-paginated-message chat-text-bubble chat-bot-bubble${pending ? ' is-loading' : ''}`}>
      {content.title && <div className="conv-paginated-title">{content.title}</div>}
      {content.displayText && <p className="conv-paginated-text">{content.displayText}</p>}

      {content.view === 'html' ? (
        <HtmlMessage html={content.html || ''} disabled />
      ) : (
        <div className="conv-paginated-scroll">
          <table>
            {columns.length > 0 && (
              <thead>
                <tr>{columns.map(column => <th key={column.key}>{column.label}</th>)}</tr>
              </thead>
            )}
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={Math.max(columns.length, 1)} className="conv-paginated-empty">No results</td></tr>
              ) : rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((column, index) => <td key={column.key}>{cellValue(row, column, index)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {handle && (hasPrev || hasNext) && (
        <div className="conv-paginated-pager">
          <button type="button" onClick={() => go(prev)} disabled={!hasPrev || Boolean(pending) || disabled}>
            Prev
          </button>
          <span aria-live="polite">{pending ? 'Loading…' : label}</span>
          <button type="button" onClick={() => go(next)} disabled={!hasNext || Boolean(pending) || disabled}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}
